import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useWorkspaceReady } from '../../auth/workspace-entry-context';
import type { ResourceScope } from '@kop/contracts';
import { Button } from '@kop/ui';
import { RefreshCw, ArrowUpRight } from 'lucide-react';
import { WeatherIcon } from './weather-icon';
import { readSelectedSiteWeatherLocation } from '../../runtime/runtime-config';
import { HubHeader } from '../hub-shell/hub-shell';
import { HomeBriefing } from './home-briefing';
import { OpenMeteoWeatherAdapter, type WeatherSnapshot } from './weather-adapter';
import type { SafetyBoardAuthority } from '../safety-board/model';
import { unresolvedFindingCountOf, type WorkHomeSnapshot } from './work-home-projection';
import { loadDashboard, type DashboardRead } from './dashboard-read';
import './operations-dashboard.css';

const weatherPort = new OpenMeteoWeatherAdapter();
const localDay = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
const number = (value: number | null | undefined) =>
  value == null ? '—' : value.toLocaleString('ko-KR');
const authorizationFailure = (error: unknown) => {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code)
      : '';
  return ['unauthenticated', 'permission-denied', 'failed-precondition'].some(
    (value) => code === value || code === `functions/${value}`,
  );
};
function Panel({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="home-panel">
      <header>
        <h2>{title}</h2>
        {note && <small>{note}</small>}
      </header>
      {children}
    </section>
  );
}

export function OperationsDashboard({
  uid,
  scope,
  featureIds,
  onOpen,
  onInspection,
  authority,
  owner,
  snapshot,
  isHq,
  children,
}: {
  uid: string;
  scope: ResourceScope;
  featureIds: readonly string[];
  onOpen: (id: string) => void;
  onInspection?: (period: { from: string; to: string } | null) => void;
  authority: SafetyBoardAuthority;
  owner: { uid: string; contractorId: string | null };
  snapshot: WorkHomeSnapshot;
  isHq: boolean;
  children?: ReactNode;
}) {
  const [day, setDay] = useState(localDay),
    [month, setMonth] = useState(() => localDay().slice(0, 7));
  const [loaded, setLoaded] = useState<{ key: string; data: DashboardRead } | null>(null),
    [busy, setBusy] = useState(false),
    [refresh, setRefresh] = useState(0);
  const [weather, setWeather] = useState<WeatherSnapshot | null>(null);
  const weatherSiteId = scope.kind === 'tenant' ? null : scope.siteId;
  const lastHandledRefresh = useRef(0);
  const revealWorkspace = useWorkspaceReady();
  const [settled, setSettled] = useState(false),
    [boardSettled, setBoardSettled] = useState(false);
  const acceptBoardSettled = useCallback(() => setBoardSettled(true), []);
  useEffect(() => {
    if (settled && boardSettled && snapshot.state !== 'loading') revealWorkspace();
  }, [settled, boardSettled, snapshot.state, revealWorkspace]);
  const featureKey = [...featureIds].sort().join('|');
  const scopeKey = JSON.stringify(scope);
  const input = useMemo(
    () => ({
      uid,
      scope: JSON.parse(scopeKey) as ResourceScope,
      day,
      month,
      features: featureKey.split('|'),
    }),
    [uid, scopeKey, day, month, featureKey],
  );
  const key = JSON.stringify(input);
  const value = loaded?.key === key ? loaded.data : null;
  const allowed = (id: string) => featureIds.includes(id);
  const open = (id: string) => {
    if (allowed(id)) onOpen(id);
  };
  useEffect(() => {
    let active = true;
    const force = refresh > lastHandledRefresh.current;
    lastHandledRefresh.current = refresh;
    setBusy(true);
    void loadDashboard(input, force)
      .then((data) => {
        if (active) setLoaded({ key: JSON.stringify(input), data });
      })
      // A transient refresh failure retains the snapshot; auth failures must immediately clear it.
      .catch((error) => {
        if (active && authorizationFailure(error)) setLoaded(null);
      })
      .finally(() => {
        if (active) {
          setBusy(false);
          setSettled(true);
        }
      });
    return () => {
      active = false;
    };
  }, [input, refresh]);
  useEffect(() => {
    let active = true;
    const location = readSelectedSiteWeatherLocation(weatherSiteId);
    setWeather(null);
    if (location)
      void weatherPort
        .current({
          source: 'active-site',
          latitude: location.latitude,
          longitude: location.longitude,
        })
        .then((result) => {
          if (active) setWeather(result);
        })
        .catch(() => { if (active) setWeather(null); });
    return () => {
      active = false;
    };
  }, [refresh, day, weatherSiteId]);
  useEffect(() => {
    let previousDay = localDay();
    const timer = window.setInterval(() => {
      const today = localDay();
      if (document.visibilityState === 'visible' && previousDay !== today) {
        setDay((selected) => (selected === previousDay ? today : selected));
        setMonth((selected) =>
          selected === previousDay.slice(0, 7) ? today.slice(0, 7) : selected,
        );
        previousDay = today;
      }
    }, 60_000);
    return () => clearInterval(timer);
  }, []);
  const findings = value?.operations?.inspection
    ? { count: value.operations.inspection.unresolved }
    : unresolvedFindingCountOf(snapshot);
  const work = value?.work,
    rows = work?.rows ?? [];
  const workSummary = value?.operations?.work;
  const activeWork =
    workSummary?.active ??
    (work?.complete
      ? rows.filter((row) =>
          ['active', 'suspended', 'completion-requested'].includes(row.lifecycle),
        ).length
      : null);
  const workTotal = owner.contractorId
    ? work?.complete
      ? rows.length
      : null
    : workSummary?.monthly;
  const tbm = value?.tbm;
  const ratio =
    tbm?.turnout && tbm.attendance != null ? Math.min(1, tbm.attendance / tbm.turnout) : 0;
  const monthLabel = `${month.slice(0, 4)}년 ${Number(month.slice(5))}월`;
  const halfLabel = `${month.slice(0, 4)}년 ${Number(month.slice(5)) <= 6 ? '상' : '하'}반기`;
  const shortcut = (id: string, label: string) =>
    allowed(id) ? (
      <Button variant="quiet" type="button" className="home-link" onClick={() => open(id)}>
        {label}
        <ArrowUpRight size={14} />
      </Button>
    ) : null;
  const stat = (
    id: string,
    label: string,
    count: number | null | undefined,
    note: string,
    action?: () => void,
  ) =>
    allowed(id) ? (
      <Button
        variant="quiet"
        className="home-stat"
        type="button"
        onClick={() => (action ? action() : open(id))}
      >
        <span>{label}</span>
        <strong>
          {number(count)}
          <small>{count != null ? '건' : ''}</small>
        </strong>
        <small>{count == null ? '관리대장에서 확인' : note}</small>
      </Button>
    ) : (
      <div className="home-stat home-stat-muted">
        <span>{label}</span>
        <small>조회 권한 없음</small>
      </div>
    );
  return (
    <section className="home-dashboard" data-workspace-dashboard="ledger">
      <HubHeader
        title="대시보드"
        pill={
          <div className="home-tools">
            <input
              className="home-control"
              type="month"
              aria-label="대시보드 조회 월"
              value={month}
              onChange={(event) => {
                if (event.target.value) setMonth(event.target.value);
              }}
            />
            <Button
              variant="quiet"
              size="compact"
              icon={<RefreshCw size={16} />}
              aria-label="대시보드 새로고침"
              title="대시보드 새로고침"
              disabled={busy}
              onClick={() => setRefresh((n) => n + 1)}
            />
          </div>
        }
      />
      <div className="home-date-weather">
        <span>
          {day.replaceAll('-', '.')}{' '}
          <small>
            {new Intl.DateTimeFormat('ko-KR', { weekday: 'long', timeZone: 'Asia/Seoul' }).format(
              new Date(`${day}T12:00:00${'+09:00'}`),
            )}
          </small>
        </span>
        <div>
          <WeatherIcon weather={weather} />
          <b>{weather ? `${weather.temperatureC}°` : ''}</b>
          <span>{weather?.condition ?? '날씨를 불러오지 못했습니다'}</span>
          <small>선택 사업장 · 현재 날씨</small>
        </div>
      </div>
      <div className="home-layout">
        <div className="home-main-grid">
          <div className="home-daily-grid">
            <Panel title="오늘 TBM" note="오늘">
              <Button
                variant="quiet"
                className="home-ring"
                type="button"
                disabled={!allowed('tbm')}
                onClick={() => open('tbm')}
              >
                <svg viewBox="0 0 160 160" aria-hidden="true">
                  <circle cx="80" cy="80" r="64" />
                  <circle
                    className="home-ring-value"
                    cx="80"
                    cy="80"
                    r="64"
                    strokeDasharray={`${402.12 * ratio} 402.12`}
                  />
                </svg>
                <span>
                  <strong>
                    {number(tbm?.attendance)}
                    <small> / {number(tbm?.turnout)}</small>
                  </strong>
                  <small>참석 / 출역{owner.contractorId ? '' : ' · 연인원'}</small>
                </span>
              </Button>
              <div className="home-ring-note">
                실시 {number(tbm?.count)}건 · 업체 {number(tbm?.companies)}개
              </div>
            </Panel>
            <div className="home-stat-grid">
              {stat(
                'work-permit',
                owner.contractorId ? '오늘 비일상작업' : '월간 비일상작업',
                workTotal,
                owner.contractorId ? '오늘 작업' : monthLabel,
              )}
              {stat('work-permit', '진행 중 작업', activeWork, '오늘 작업')}
              {stat(
                'inspection-reporting',
                '월간 점검',
                value?.operations?.inspection?.monthly,
                value?.operations?.inspection
                  ? `${Number(month.slice(5))}월 · 누적 ${number(value.operations.inspection.registered)}건`
                  : monthLabel,
                onInspection
                  ? () =>
                      onInspection({
                        from: `${month}-01`,
                        to: new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)), 0))
                          .toISOString()
                          .slice(0, 10),
                      })
                  : undefined,
              )}
              {stat(
                'inspection-reporting',
                '현재 미조치',
                findings.count,
                '이전 발생 건 포함',
                onInspection ? () => onInspection(null) : undefined,
              )}
            </div>
            <Panel title="작업 진행 현황" note="오늘">
              <div className="home-work-states">
                {[
                  [
                    '준비',
                    workSummary?.preparing ??
                      (work?.complete
                        ? rows.filter(
                            (row) =>
                              ![
                                'active',
                                'suspended',
                                'completion-requested',
                                'closed',
                                'invalidated',
                              ].includes(row.lifecycle),
                          ).length
                        : null),
                  ],
                  ['진행', activeWork],
                  [
                    '중지',
                    workSummary?.suspended ??
                      (work?.complete
                        ? rows.filter((row) => row.lifecycle === 'suspended').length
                        : null),
                  ],
                  [
                    '종료',
                    workSummary?.closed ??
                      (work?.complete
                        ? rows.filter((row) => row.lifecycle === 'closed').length
                        : null),
                  ],
                ].map(([label, count]) => (
                  <Button
                    variant="quiet"
                    key={label}
                    className="home-work-state"
                    type="button"
                    disabled={!allowed('work-permit')}
                    onClick={() => open('work-permit')}
                  >
                    <span>{label}</span>
                    <strong>{number(count as number | null)}</strong>
                  </Button>
                ))}
              </div>
              <div className="home-panel-foot">
                {shortcut('work-permit', '작업허가 · TBM · 순회점검')}
              </div>
            </Panel>
          </div>
          <Panel
            title={isHq ? '선택 사업장 일일 작업' : '오늘 비일상작업'}
            note={
              workSummary
                ? `오늘 ${number(workSummary.total)}건 · ${day.slice(5).replace('-', '.')}`
                : '오늘'
            }
          >
            <div className="home-table" data-bounded-content>
              <table>
                <thead>
                  <tr>
                    <th>시간</th>
                    <th>업체</th>
                    <th>작업명</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.workId}>
                      <td>
                        {new Date(row.startsAt).toLocaleTimeString('ko-KR', {
                          timeZone: 'Asia/Seoul',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </td>
                      <td>{row.contractorName}</td>
                      <td>{shortcut('work-permit', row.title)}</td>
                      <td>
                        {row.lifecycle === 'active'
                          ? '진행'
                          : row.lifecycle === 'closed'
                            ? '종료'
                            : row.lifecycle === 'suspended'
                              ? '중지'
                              : '확인 필요'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!rows.length && (
                <p className="home-empty">
                  {work?.complete
                    ? '오늘 예정된 작업이 없습니다.'
                    : '작업 관리대장에서 현황을 확인해 주세요.'}
                </p>
              )}
            </div>
          </Panel>
          <div className="home-period-heading">
            <h2>기간별 관리 현황</h2>
            <span>{monthLabel} 기준</span>
          </div>
          <div className="home-period-grid">
            <Panel title="위험성평가" note={halfLabel}>
              <div className="home-period-body" data-bounded-content>
                <strong className="home-big-number">
                  {number(value?.risk?.complete ? value.risk.total : null)}
                  <small>건</small>
                </strong>
                {value?.operations?.risk && (
                  <p>전체 등록 {number(value.operations.risk.registered)}건</p>
                )}
                <div className="home-pair">
                  <span>작성 중</span>
                  <b>{number(value?.risk?.complete ? value.risk.draft : null)}</b>
                </div>
                {shortcut('regular-risk', '평가 · 근로자 의견 확인')}
              </div>
            </Panel>
            <Panel title="교육 · 비상훈련" note={monthLabel}>
              <div className="home-period-body home-period-links" data-bounded-content>
                <div className="home-pair">
                  <span>{shortcut('safety-training', '교육 실시')}</span>
                  <b>{number(value?.operations?.education?.total)}건</b>
                </div>
                <small>
                  교육 참석 {number(value?.operations?.education?.attendance)}명 · 연인원
                </small>
                <div className="home-pair">
                  <span>{shortcut('evacuation-drill', '훈련 계획 / 결과')}</span>
                  <b>
                    {number(value?.operations?.drill?.planned)} /{' '}
                    {number(value?.operations?.drill?.completed)}건
                  </b>
                </div>
                {value?.operations?.education && (
                  <small>전체 교육 기록 {number(value.operations.education.registered)}건</small>
                )}
              </div>
            </Panel>
            <Panel title="재해자 누계" note="전체 관리대장">
              <div className="home-period-body" data-bounded-content>
                <div className="home-pair">
                  <span>{owner.contractorId ? '우리 업체' : '선택 사업장'} · 재해자</span>
                  <b>{number(value?.victimTotal)}</b>
                </div>
                {shortcut('incident', '재해자 관리대장')}
              </div>
            </Panel>
          </div>
        </div>
        <div className="home-side-grid">
          <HomeBriefing siteId={'siteId' in scope ? scope.siteId : null} authority={authority} owner={owner} onSettled={acceptBoardSettled} />
          <Panel title="우선 확인할 업무">
            <div className="home-task-list" data-bounded-content>
              <div>
                {shortcut('work-permit', '작업 시작 전 확인')}
                <small>작업허가 · TBM · 현장 준비</small>
              </div>
              <div>
                {shortcut('tbm', 'TBM 실시 · 참석 확인')}
                <small>오늘 작업내용과 위험요인 전달</small>
              </div>
              <div>
                {shortcut('inspection-reporting', '미조치 지적사항')}
                <small>
                  {findings.count == null
                    ? '조치 현황 확인'
                    : `${findings.count}건 · 이전 발생 건 포함`}
                </small>
              </div>
            </div>
          </Panel>
          <Panel title="자주 쓰는 업무">
            <div className="home-favorites" data-bounded-content>
              {children}
            </div>
          </Panel>
        </div>
      </div>
    </section>
  );
}
